// The permission map. One file, read by both the server and the browser.
//
// A role is a row in GRANTS. It names the views that role may open and the
// actions it may take. Everything downstream is derived from it:
//
//   - the navigation renders from VIEWS filtered by canView
//   - the client route guards call canView
//   - the server route guards are built from requireView and requireAction
//
// Adding a role means adding one entry to GRANTS and one entry to ROLES. No
// component and no route handler needs to change. Comparing req.user.role to a
// string anywhere else in the codebase is the thing this file exists to stop.
//
// This file is plain ESM with no imports so the same source serves Node and
// Vite. Keep it that way.

export const ROLE = {
  ADMIN: 'ADMIN',
  FRANCHISEE: 'FRANCHISEE',
}

// Views are top level destinations. The order here is the navigation order,
// left to right, for every role. A role that cannot open a view never renders
// its tab. Activity leads: it is the shared dashboard every role lands on.
export const VIEWS = [
  // A clinic's Activity is what the accounts have produced for its own
  // clinics, so the tab reads My Activity for that role. Same view, same
  // route; the server scopes the figures.
  {
    key: 'activity',
    path: '/activity',
    label: 'Activity',
    labels: { [ROLE.FRANCHISEE]: 'My Activity' },
  },
  { key: 'customers', path: '/customers', label: 'Customers' },
  { key: 'onsites', path: '/onsites', label: 'Onsites' },
  { key: 'franchisees', path: '/franchisees', label: 'Franchise Owners' },
  { key: 'tests', path: '/tests', label: 'Tests' },
  { key: 'reports', path: '/reports', label: 'Reports' },
  { key: 'users', path: '/admin/users', label: 'Users' },
  { key: 'settings', path: '/admin/settings', label: 'Settings' },
]

// Actions are things a role may do beyond opening a view. Reading a view's own
// list is covered by the view itself, so only writes and the narrower reads
// appear here.
export const ACTION = {
  CUSTOMERS_WRITE: 'customers.write',
  // The monthly activity import: uploading the month's documents, reviewing
  // what the model extracted, and committing it. Admin only, like every way
  // data enters or leaves the system wholesale.
  ACTIVITY_IMPORT: 'activity.import',
  ONSITES_WRITE: 'onsites.write',
  // Filing the completed on-site summary, and attaching its documents, on an
  // onsite the user can read. Staff hold it beside onsites.write; a
  // franchisee holds it for their own clinics' jobs, which is the whole of
  // what they may change on an onsite. The request itself stays staff's.
  ONSITES_REPORT: 'onsites.report',
  // Reading every onsite. Without it, the Onsites view is scoped to the
  // viewer's own franchisee: a franchisee user sees the jobs they performed
  // and nothing else. Admin sees the whole queue.
  ONSITES_READ_ALL: 'onsites.readAll',
  // Reading every activity line. Without it, the Activity view is My
  // Activity: only the lines attributed to the viewer's own clinics, and
  // only the customers those clinics have worked.
  ACTIVITY_READ_ALL: 'activity.readAll',
  // Customer bills: what FTFS billed the customer. Admin only, through
  // ALL_ACTIONS. Every other role reads franchisee invoices alone, and the
  // server leaves the FTFS figures out of their responses entirely.
  CUSTOMER_BILLS_READ: 'activity.customerBills',
  CUSTOMERS_MANAGE_SPLIT: 'customers.manageSplit',
  // Negotiated per-customer prices, and the revenue split beside them. Admin
  // sets both today; they stay separate actions so a later role can price a
  // customer without touching the split. Every role reads the split: a
  // clinic's bill rates reflect it.
  CUSTOMERS_MANAGE_PRICING: 'customers.managePricing',
  // Clinic bill rates: what the franchisee bills FTFS per test for a
  // customer. Admin enters them, through ALL_ACTIONS; every role with the
  // Customers view reads them, which is how a clinic knows what to bill us.
  BILL_RATES_WRITE: 'billRates.write',
  // Reading the test catalog, apart from managing it through the Tests view.
  // Both are admin's today; the split stays so a later role can price a
  // customer without editing the catalog.
  TESTS_LIST: 'tests.list',
  CATALOG_WRITE: 'catalog.write',
  // Editing a customer means assigning collection sites, which needs the
  // franchisee names. Full franchisee detail is a separate action, because
  // only an admin manages the roster itself.
  FRANCHISEES_LIST: 'franchisees.list',
  FRANCHISEES_WRITE: 'franchisees.write',
  USERS_MANAGE: 'users.manage',
  SETTINGS_MANAGE: 'settings.manage',
  // The issue log is internal team material. Customers are globally visible,
  // but the problems logged against a relationship, and the recency signal
  // beside them, are not for franchisee eyes.
  ISSUES_READ: 'issues.read',
  ISSUES_WRITE: 'issues.write',
  // A customer's contacts and collection sites. The profile is otherwise open
  // to every role with the Customers view, but these are the account's people
  // and locations, which a clinic does not need: a clinic sees the account
  // information, the protocol and the activity.
  CUSTOMERS_CONTACTS_READ: 'customers.contactsRead',
  // The audit log names who did what across the whole system, including
  // admin-only ground like splits and user management, so reading it is
  // admin's alone even though the Reports view itself is shared.
  AUDIT_READ: 'audit.read',
  // Removing things: customers, contacts, sites, price rows, issues,
  // activity, imports. Only an admin destroys one, and a later day-to-day
  // role would not. Every DELETE route checks this, not the write action.
  RECORDS_DELETE: 'records.delete',
  // Seeing the app as a clinic would. Admin only, through ALL_ACTIONS: the
  // session temporarily carries the franchisee role on the server, so every
  // guard, scope and screen behaves exactly as a clinic sign-in, and the
  // toggle back is guarded on the real role.
  VIEW_AS: 'view.as',
  // The in-app feedback widget. Granted to nobody below, so admin alone holds
  // it through ALL_ACTIONS. The whole feature is deliberately removable; see
  // "Removing the feedback widget" in DECISIONS.md. Deleting this line is one
  // of the removal steps.
  FEEDBACK: 'feedback.manage',
}

const ALL_VIEWS = VIEWS.map((view) => view.key)
const ALL_ACTIONS = Object.values(ACTION)

const GRANTS = {
  [ROLE.ADMIN]: {
    // Everything. The Franchisor role, which held the day-to-day work without
    // the admin ground, was folded into Admin in October 2026. A narrower
    // third role is expected later, which is why the actions above stay
    // split finer than two roles need.
    views: ALL_VIEWS,
    actions: ALL_ACTIONS,
  },
  [ROLE.FRANCHISEE]: {
    // Reading is the grant. The activity dashboard, the customer directory
    // and the clinic roster are global: a franchisee sees every account and
    // every clinic's opt-in status. Onsites is the exception: without
    // ONSITES_READ_ALL the view shows only the jobs linked to the user's own
    // clinics. Their own clinics they can also edit (minus the admin-only
    // fields) and opt in or out of onsites; both are ownership rules enforced
    // in the franchisees routes, not grants. On their own onsites they file
    // the completed on-site summary and its documents, never the request.
    views: ['activity', 'customers', 'onsites', 'franchisees'],
    actions: [ACTION.ONSITES_REPORT],
  },
}

export const ROLES = [
  {
    value: ROLE.ADMIN,
    label: 'Admin',
    scope: 'Everything, including deletes, the activity import, users and settings',
  },
  {
    value: ROLE.FRANCHISEE,
    label: 'Franchisee',
    scope:
      'Activity, customers and the clinic roster; their own onsites, with the completed on-site summary, and clinic record',
  },
]

export const ROLE_VALUES = ROLES.map((role) => role.value)
export const ROLE_LABEL = Object.fromEntries(ROLES.map((role) => [role.value, role.label]))
export const ROLE_SCOPE = Object.fromEntries(ROLES.map((role) => [role.value, role.scope]))

// An unknown role grants nothing. A typo in a role name must fail closed.
const grantsFor = (role) => GRANTS[role] ?? { views: [], actions: [] }

export const canView = (role, view) => grantsFor(role).views.includes(view)
export const can = (role, action) => grantsFor(role).actions.includes(action)

// The tabs this role sees, already in order.
export const navFor = (role) =>
  VIEWS.filter((view) => canView(role, view.key)).map((view) => ({
    to: view.path,
    label: view.labels?.[role] ?? view.label,
  }))

// The view a path belongs to: the longest view path that prefixes it.
export const viewForPath = (pathname) =>
  VIEWS.filter((view) => pathname.startsWith(view.path)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0] ?? null

// Where a role lands after signing in: the leftmost view it can open, which
// is the activity dashboard for every role.
export const homePathFor = (role) => navFor(role)[0]?.to ?? '/activity'
